/*
 * cpu.xhey.top session_token 调度
 *
 * 职责：
 *   - exchangeNow(): 阻塞式调 <XHEY_AI_BASE>/auth/service-login，落 store
 *   - getValidToken(): 主入口；返回可用 token，必要时 await exchange
 *       · 没缓存 / 过期 / group 或 user 变了 → 阻塞换
 *       · 剩 < 1 天 → 后台静默换，立刻返老 token
 *   - clear(): 登出 / group 切换时清缓存
 *
 * 并发保护：同一时刻只允许一个 exchange 在飞，重复请求复用同一 Promise。
 */

import { store } from '../../../app/store'
import { tokenStore } from './tokenStore'
import type { CachedToken } from './tokenStore'
import { XHEY_AI_BASE } from '../config'

interface ServiceLoginResponse {
  session_token: string
  expires_in: number
  user?: {
    user_id?: string
    mobile?: string
    nickname?: string
    headimgurl?: string
    groups?: Array<{ group_id: string }>
  }
}

interface UserContext {
  mobile: string
  user_id: string
  group_id: string
  /** 团队短 id（group.invite_id），对应后端 team_no 字段 */
  team_no: string
  nickname?: string
  headimgurl?: string
}

/** 从 redux store 拉当前用户的 service-login 入参 */
function readUserContext(): UserContext | null {
  try {
    const state: any = store.getState()
    const user = state?.main?.user
    const group = state?.group
    if (!user || !group) return null
    const mobile = user.mobile
    const user_id = user.user_id
    const nickname = user.nickname
    const headimgurl = user.headimgurl
    const group_id = group.groupId
    const invite_id = group.invite_id
    if (!mobile || !user_id || !group_id) {
      // eslint-disable-next-line no-console
      console.warn('[cpu-token] 用户上下文不全', {
        mobile,
        user_id,
        group_id
      })
      return null
    }
    return {
      mobile,
      user_id,
      group_id,
      // team_no = group.invite_id（团队短 id），后端字段名是 team_no
      team_no: invite_id || '',
      nickname: nickname || undefined,
      headimgurl: headimgurl || undefined
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[cpu-token] readUserContext 失败', e)
    return null
  }
}

let inflight: Promise<CachedToken> | null = null

async function callServiceLogin(ctx: UserContext): Promise<CachedToken> {
  const res = await fetch(`${XHEY_AI_BASE}/auth/service-login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      mobile: ctx.mobile,
      user_id: ctx.user_id,
      group_id: ctx.group_id,
      team_no: ctx.team_no,
      ...(ctx.nickname ? { nickname: ctx.nickname } : {}),
      ...(ctx.headimgurl ? { headimgurl: ctx.headimgurl } : {})
    })
  })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(`service-login HTTP ${res.status}: ${detail.slice(0, 300)}`)
  }
  const json = (await res.json()) as ServiceLoginResponse
  if (!json.session_token) {
    throw new Error('service-login 响应缺 session_token')
  }
  const cached: CachedToken = {
    token: json.session_token,
    // expires_in 是秒；预留 60s buffer 防边界
    expiresAt: Date.now() + (json.expires_in - 60) * 1000,
    groupId: ctx.group_id,
    userId: ctx.user_id
  }
  tokenStore.set(cached)
  return cached
}

/** 阻塞式换 token；并发去重 */
function exchangeNow(): Promise<CachedToken> {
  if (inflight) return inflight
  const ctx = readUserContext()
  if (!ctx) {
    return Promise.reject(
      new Error('当前用户信息未就绪，无法获取 AI 服务 token')
    )
  }
  inflight = callServiceLogin(ctx).finally(() => {
    inflight = null
  })
  return inflight
}

/** 触发后台静默续签（不抛错、不阻塞调用方）*/
function scheduleRefresh(): void {
  if (inflight) return
  exchangeNow().catch((e) => {
    // eslint-disable-next-line no-console
    console.warn('[cpu-token] 后台续签失败', e)
  })
}

/** 主入口：保证返回一个可用的 token */
export async function getValidToken(): Promise<string> {
  const ctx = readUserContext()
  const cached = tokenStore.get()

  // 检查 user / group 是否还匹配；不匹配等于 token 失效
  const stale =
    !cached ||
    !ctx ||
    cached.userId !== ctx.user_id ||
    cached.groupId !== ctx.group_id ||
    tokenStore.isExpired()

  if (stale) {
    if (cached) tokenStore.clear()
    const fresh = await exchangeNow()
    return fresh.token
  }

  // 还能用，但快过期了 → 后台续，立刻返老 token
  if (tokenStore.needsRefresh()) scheduleRefresh()
  return cached!.token
}

/** 强制失效当前 token（401 / 切 group / 登出） */
export function invalidateToken(): void {
  tokenStore.clear()
}

/**
 * 同步取 cached token (无续签 / 无 fetch). 给 pagehide / unmount 这种同步阶段
 * 用 — getValidToken 是 async, unload 期间 await 不可靠.
 * 拿不到 (没 cached / 过期) 返空, 调用方 swallow 失败 (best-effort).
 */
export function getCachedTokenSync(): string {
  const cached = tokenStore.get()
  if (!cached || tokenStore.isExpired()) return ''
  return cached.token
}
